import type { SimpleCitation } from './answers';
import { isFileCitation } from './citationGuards';
import type { ViewerSource } from './viewerSource';

// A sentinel origin so a relative proxy path parses; only `.pathname` is read,
// so the host is never observable. Its own literal rather than the app URL
// predicates' one — two independent sentinels, not one shared identity.
const INTEGRATION_PARTNER_PROXY_URL_BASE = 'https://integration-partner-proxy.local';
const INTEGRATION_PARTNER_PROXY_PATH =
  /\/api\/(?:v1|v2|v2-1|v2-2)\/ml-scribe\/evidence\/integration-partners\/([^/]+)\/[^/]+\/(?:html|read|download)\/?$/;

interface PublisherIdentity {
  readonly domains: readonly string[];
  /** Short label beside a favicon. Abbreviations stay uppercase. */
  readonly shortName: string;
  /** Full title for reference rows. Omitted when the short name is already the title. */
  readonly fullName?: string;
  /** Case-insensitive labels the backend sends when the URL host is not the publisher domain. */
  readonly aliases: readonly string[];
}

const PUBLISHERS: readonly PublisherIdentity[] = [
  {
    domains: ['nejm.org'],
    shortName: 'NEJM',
    fullName: 'The New England Journal of Medicine',
    aliases: ['nejm', 'the new england journal of medicine'],
  },
  {
    domains: ['jamanetwork.com'],
    shortName: 'JAMA',
    aliases: [],
  },
  {
    domains: ['onlinelibrary.wiley.com'],
    shortName: 'Wiley',
    aliases: [],
  },
  {
    domains: ['cochranelibrary.com'],
    shortName: 'Cochrane Library',
    aliases: [],
  },
  {
    domains: ['link.springer.com'],
    shortName: 'Springer Nature',
    aliases: [],
  },
  {
    domains: ['nature.com'],
    shortName: 'Nature',
    aliases: [],
  },
];

function matchPublisherHost(host: string): PublisherIdentity | undefined {
  const normalized = host.toLowerCase().replace(/^www\./, '');
  return PUBLISHERS.find((publisher) =>
    publisher.domains.some(
      (domain) => normalized === domain || normalized.endsWith(`.${domain}`)
    )
  );
}

function publisherFromUrl(url: string | undefined): PublisherIdentity | undefined {
  if (!url) return undefined;
  try {
    return matchPublisherHost(new URL(url).hostname);
  } catch {
    return undefined;
  }
}

/** Canonical short publisher label, including saved citations with domain-only labels. */
export function getPublisherDisplayName(url: string | undefined): string | undefined {
  return publisherFromUrl(url)?.shortName;
}

/**
 * Resolve a publisher from the URL, the enriched domain, or a known label.
 * Backend display names sometimes arrive title-cased ("Nejm") on a DOI URL,
 * which misses a host match.
 */
export function resolvePublisherIdentity(source: {
  url?: string;
  domain?: string;
  displayName?: string;
  websiteName?: string;
}): PublisherIdentity | undefined {
  const fromUrl = publisherFromUrl(source.url);
  if (fromUrl) return fromUrl;
  if (source.domain) {
    const fromDomain = matchPublisherHost(source.domain);
    if (fromDomain) return fromDomain;
  }
  const labels = [source.displayName, source.websiteName]
    .filter((label): label is string => Boolean(label?.trim()))
    .map((label) => label.trim().toLowerCase());
  if (labels.length === 0) return undefined;
  return PUBLISHERS.find((publisher) =>
    publisher.aliases.some((alias) => labels.includes(alias))
  );
}

/** Logical domains for Heidi HTML proxy paths when the BE omits `domain`. */
export const PARTNER_PROXY_DOMAINS: Readonly<Record<string, string>> = {
  mims: 'mims.com.au',
};

/**
 * Normalize domain by stripping www. prefix for consistent lookups
 */
export function normalizeDomain(domain: string): string {
  return domain.replace(/^www\./, '');
}

/** Extract the partner key from a Heidi integration-partner proxy URL, if present. */
export function inferPartnerKeyFromProxyUrl(url: string): string | null {
  try {
    const pathname = new URL(url, INTEGRATION_PARTNER_PROXY_URL_BASE).pathname;
    const match = pathname.match(INTEGRATION_PARTNER_PROXY_PATH);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

/** Resolve the logical partner domain for opaque Heidi proxy paths. */
export function getLogicalDomainFromProxyUrl(url: string): string | null {
  const partnerKey = inferPartnerKeyFromProxyUrl(url);
  if (!partnerKey) return null;
  return PARTNER_PROXY_DOMAINS[partnerKey] ?? null;
}

/**
 * Extract domain from URL, handling errors gracefully.
 * Returns normalized domain (www. prefix stripped).
 */
export function getDomainFromUrl(url: string): string {
  const proxyDomain = getLogicalDomainFromProxyUrl(url);
  if (proxyDomain) return proxyDomain;

  try {
    const hostname = new URL(url).hostname;
    return normalizeDomain(hostname);
  } catch {
    // For malformed URLs, try to extract domain-like string
    const match = url.match(/(?:https?:\/\/)?(?:www\.)?([^/]+)/);
    return match?.[1] ? normalizeDomain(match[1]) : url;
  }
}

/**
 * Format a display name from a URL domain.
 * Extracts the main domain part and capitalizes it for display.
 *
 * @example
 * getFormattedSourceName('https://pubmed.ncbi.nlm.nih.gov/123') // 'Pubmed'
 * getFormattedSourceName('https://www.cdc.gov/page') // 'Cdc'
 */
export function getFormattedSourceName(url: string): string {
  if (!url) return '';

  const domain = getDomainFromUrl(url);
  // Extract the main part before the first dot (e.g., 'pubmed' from 'pubmed.ncbi.nlm.nih.gov')
  const mainPart = domain.split('.')[0] || domain;
  // Capitalize first letter
  return mainPart.charAt(0).toUpperCase() + mainPart.slice(1);
}

// Known trusted medical source domains for citation display
const KNOWN_SOURCES = new Set([
  'pubmed.ncbi.nlm.nih.gov',
  'ncbi.nlm.nih.gov',
  'nih.gov',
  'cdc.gov',
  'who.int',
  'fda.gov',
  'cochranelibrary.com',
  'uptodate.com',
  'medlineplus.gov',
  'mayoclinic.org',
  'clevelandclinic.org',
  'webmd.com',
  'healthline.com',
  'jamanetwork.com',
  'nejm.org',
  'thelancet.com',
  'bmj.com',
  'nature.com',
  'springer.com',
  'wiley.com',
  'sciencedirect.com',
  'aafp.org',
  'ama-assn.org',
]);

/**
 * Check if a source's websiteType indicates it's a guideline publisher.
 */
export function isGuidelineSource(websiteType?: string[]): boolean {
  if (!websiteType?.length) return false;
  return websiteType.some((t) => t.toLowerCase().includes('guideline'));
}

/**
 * Check if a URL is from a known/trusted medical source.
 * Used to display trust indicators in citations.
 */
export function isKnownSource(url: string): boolean {
  if (!url) return false;

  const domain = getDomainFromUrl(url);

  // Check if domain matches exactly or is a subdomain of a known source
  for (const knownDomain of KNOWN_SOURCES) {
    if (domain === knownDomain || domain.endsWith(`.${knownDomain}`)) {
      return true;
    }
  }

  return false;
}

/**
 * Resolve the human-readable name to display in the source viewer header /
 * citation badge. Prefers backend-enriched `website_name`, then
 * `display_name`, then URL hostname; finally falls back to a generic
 * "Source" label so we always render something.
 */
export function getDisplayName(source: SimpleCitation): string {
  const publisher = resolvePublisherIdentity({
    url: source.url,
    domain: source.domain,
    displayName: source.display_name,
    websiteName: source.website_name,
  });
  if (publisher && !isFileCitation(source.source_type)) return publisher.shortName;
  if (source.website_name) return source.website_name;
  if (source.display_name) return source.display_name;
  return getDomainFromUrl(source.url || '') || 'Source';
}

/**
 * Build a canonical `ViewerSource` payload from a citation. Centralised so
 * every entry point into the side panel (citation badge, sources popover,
 * references list, partner CTA) produces consistent state. Specifically:
 *
 * - Prefers BE-provided `domain` over URL hostname extraction so partner
 *   sources on opaque hosts (e.g. MIMS on presigned S3 URLs) still show
 *   their logical domain (`mims.com.au`) in the viewer chrome.
 * - Threads `source_id` through as `id` so the sheet's close handler can
 *   call `markSourceAsRead(id, elapsed)` instead of URL-keyed tracking —
 *   which would fragment reads across rotating presigned URLs.
 * - Forwards the BE `embed` hint so legacy absolute partner HTML can opt into
 *   inline rendering; Heidi backend source/read/html URLs are detected by URL
 *   shape even when the hint is absent.
 *
 * The caller is responsible for ensuring `source.url` is defined; the
 * side panel cannot render without a URL.
 *
 * `resolveIconUrl` is INJECTED rather than built here. The icon used to be a
 * hardcoded third-party favicon URL, which an airgapped on-prem build must not
 * request, and this zone is inert — it can neither read the build profile nor
 * import the `@heidi/utils` resolver that knows the answer. So the caller, which
 * can, passes `getDomainFaviconUrl`; a resolver that returns `undefined` leaves
 * `iconUrl` unset and the viewer header falls back to the domain initial.
 */
export function buildViewerSource(
  source: SimpleCitation,
  resolveIconUrl: (domain: string) => string | undefined
): ViewerSource {
  const url = source.url || '';
  const domain = source.domain || getDomainFromUrl(url) || '';
  return {
    id: source.source_id,
    url,
    title: source.title || '',
    domain,
    sourceName: getDisplayName(source),
    iconUrl: domain ? resolveIconUrl(domain) : undefined,
    embed: source.embed,
  };
}
